import {
  Component,
  OnInit,
  ViewChild,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { SharedConstants } from '@shared/model/shared-constants';
import { MatSort } from '@angular/material/sort';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { LayoutService } from '@shared/services/layout.service';
import { User } from '../../../users/models/user';
import { take, takeUntil } from 'rxjs/operators';
import { UserStatus } from '@shared-features/enums/user-status.enum';
import { Status } from '@shared/enums/status.enum';
import { BaseComponent } from '@shared/components/base-component/base.component';
import { UserType } from '@shared-features/enums/user-type.enum';
import { ConfigConstant } from '@shared/constants/config.constant';
import { ActivatedRoute } from '@angular/router';
import { ConfirmationService } from '@shared/services/confirmation.service';
import { ConfirmationType } from '@shared/model/confirmation';
import { Confirmation } from '@shared/model/confirmation';
import { RolesConstants } from '@shared/model/roles-constants';
import { UsersApiService } from '@features/users/services/users-api.service';
import { UsersCommonApiService } from '@shared-features/services/users-common-api.service';
import { OrganizationsApiService } from '@features/organization/services/organizations-api.service';
import { GenerateComplexPasswordDialog } from '@shared/components/generate-complex-password-dialog/generate-complex-password-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { UpdateUserDto } from '@features/users/dto/update-user.dto';
@Component({
  selector: 'app-users-list',
  templateUrl: './users-list.component.html',
  styleUrls: ['./users-list.component.scss'],
})
export class UsersListComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() userType: UserType;
  @Input() readonly = false;
  @Input() organizationUsers: User[];
  @Input() id: number;
  @Output() showDetails = new EventEmitter();
  @ViewChild('resetPasswordModal') resetPasswordModal;

  dataSource = new MatTableDataSource<User>();
  @ViewChild(MatSort, { static: true }) sort: MatSort;

  public displayedColumns: string[] = [
    'code',
    'name',
    'email',
    'mobileNumber',
    'roles',
    'status',
    'shippingType',
    'allowToLogin',
    'updatedAt',
    'action',
  ];

  userStatus = UserStatus;
  sharedConstants = SharedConstants;
  RolesConstants = RolesConstants;
  currentUsers: User[] = [];
  originalUsers: User[] = [];
  filteredUsers: User[] = [];
  //pagination
  pageSize: number = ConfigConstant.PAGE_SIZE;
  pageIndex: number = 1;
  offset: number = ConfigConstant.INIT_PAGE_OFFSET;
  totalRowsCount: number;

  Status = Status;
  UserType = UserType;

  constructor(
    public layoutService: LayoutService,
    private activatedRoute: ActivatedRoute,
    private confirmationService: ConfirmationService,
    private usersApiService: UsersApiService,
    private usersCommonApiService: UsersCommonApiService,
    private organizationsApiService: OrganizationsApiService,
    private MatDialog: MatDialog
  ) {
    super(['ConfirmActivateResetPassword', 'ConfirmApproveUser']);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.id && changes?.id.currentValue !== changes.id.previousValue) this.handleIdChange();
  }

  ngOnInit(): void {
    if (this.activatedRoute.snapshot.params?.id) {
      this.id = this.activatedRoute.snapshot.params?.id;
    }
    this.handleIdChange();
  }

  private handleIdChange(): void {
    if (this.userType !== UserType.ORGANIZATION) {
      this.getAllUsers();
    } else {
      this.setupTable(this.organizationUsers);
    }
  }

  private getAllUsers() {
    this.load(this.usersCommonApiService.getAllUsers(this.userType, this.id), {
      fetchOnce: true,
      emptyContentCheck: (res) => Array.isArray(res) && !res.length,
      isLoadingTransparent: true,
    }).subscribe((res) => {
      this.setupTable(res);
    });
  }
  onSearch(value: string) {
    this.filteredUsers = value
      ? this.originalUsers.filter(
          (user) =>
            user.name.toLowerCase().trim().includes(value.trim().toLowerCase()) ||
            user.email?.toLowerCase().trim().includes(value.trim().toLowerCase()) ||
            user.mobileNumber?.toLowerCase().trim().includes(value.trim().toLowerCase()) ||
            user.status.toLowerCase().trim().includes(value.trim().toLowerCase())
        )
      : this.originalUsers;
    this.onPageIndexChange(ConfigConstant.INIT_PAGE_INDEX);
    this.totalRowsCount = this.filteredUsers.length;
  }

  onSelectStatus(filterValue: string) {
    this.filteredUsers = filterValue
      ? this.originalUsers.filter((user) => user.status === filterValue)
      : this.originalUsers;
    this.totalRowsCount = this.filteredUsers.length;
    this.onPageIndexChange(ConfigConstant.INIT_PAGE_INDEX);
  }

  onPageIndexChange(index: number) {
    this.pageIndex = index;
    this.offset = (this.pageIndex - 1) * this.pageSize;
    this.currentUsers = this.getCurrentUsers(this.offset, this.pageSize * this.pageIndex);
    this.dataSource.data = this.currentUsers;
  }

  setupTable(users: User[]) {
    this.originalUsers = users;
    this.filteredUsers = this.originalUsers;
    this.currentUsers = this.getCurrentUsers(0, this.pageSize);
    this.dataSource.data = this.currentUsers;
    this.totalRowsCount = this.originalUsers.length;
    this.dataSource.sort = this.sort;
    this.sort.direction = 'desc';
    this.sort.active = 'updatedAt';
    this.sort.sortChange.emit(this.sort);
    this.dataSource.sortingDataAccessor = (data, sortHeaderId) =>
      data[sortHeaderId].toString().toLocaleLowerCase();
  }

  getCurrentUsers(fromIndex: number, toIndex: number): User[] {
    return this.filteredUsers?.slice(fromIndex, toIndex);
  }

  onActivateResetPasswordOptionClicked(id: number) {
    this.confirmationService
      .confirm(
        new Confirmation(
          ConfirmationType.NonDestructiveAction,
          this.translationsList['ConfirmActivateResetPassword']['Title'],
          this.translationsList['ConfirmActivateResetPassword']['Message']
        )
      )
      .then((value) => {
        if (value) {
          this.activateAndResetPassword(id);
        }
      });
  }

  private activateAndResetPassword(id: number) {
    this.load(this.usersCommonApiService.activateAndResetPassword(id, UserType.USER), {
      isLoadingTransparent: true,
      fetchOnce: true,
      emptyContentCheck: (res) => res === null || res === undefined,
    }).subscribe(() => {
      this.showSuccessMessage('SuccessMessages.PasswordResetSuccessfully');
      if (this.userType !== UserType.ORGANIZATION) {
        this.getAllUsers();
      } else {
        this.getOrganizationById();
      }
    });
  }

  private getOrganizationById() {
    this.load(this.organizationsApiService.getOrganizationById(this.id), {
      isLoadingTransparent: true,
    })
      .pipe()
      .subscribe((organization) => {
        this.setupTable(organization.users);
      });
  }

  onApproveUserClicked(user: User) {
    this.confirmationService
      .confirm(
        new Confirmation(
          ConfirmationType.NonDestructiveAction,
          this.translationsList['ConfirmApproveUser']['Title'] + ' ' + user?.name,
          this.translationsList['ConfirmApproveUser']['Message']
        )
      )
      .then((value) => {
        if (value) {
          const updatedUser = new User();
          updatedUser.id = user.id;
          updatedUser.status = UserStatus.ACTIVE;
          const updateUserPayload = new UpdateUserDto(updatedUser);
          this.load(this.usersApiService.updateUser(updateUserPayload))
            .pipe(take(1))
            .subscribe(() => {
              this.showSuccessMessage('SuccessMessages.UserUpdatedSuccessfully');
              if (this.userType !== UserType.ORGANIZATION) {
                this.getAllUsers();
              } else {
                this.setupTable(this.organizationUsers);
              }
            });
        }
      });
  }

  onResetPasswordOptionClicked(user: User) {
    this.load(
      this.usersCommonApiService.resetPassword({
        id: user.id,
        type: UserType.USER,
      }),
      {
        isLoadingTransparent: true,
        successMessagePath: 'SuccessMessages.EmailSent',
      }
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe((_) => {});
  }

  openGenerateComplexPassword(userId: number) {
    const dialog = this.MatDialog.open(GenerateComplexPasswordDialog, {
      data: { userId },
      width: '400px',
    });
  }
}
